'use client';

import { useMutation } from '@tanstack/react-query';
import Image from 'next/image';
import { useState, type FormEvent } from 'react';

import {
  MEASUREMENT_BOUNDS,
  SizeAdviceSchema,
  type MeasurementKey,
  type Product,
  type SizeAdvice,
} from '@/entities/product';
import { browserApiRequest } from '@/shared/api';
import { cx } from '@/shared/lib/cx';
import { Button } from '@/shared/ui/button';
import { Dialog } from '@/shared/ui/dialog';
import { Icon } from '@/shared/ui/icon';
import { ProgressBar } from '@/shared/ui/progress-bar';
import { Spinner } from '@/shared/ui/spinner';

import styles from './size-advisor.module.scss';

type Fit = Record<MeasurementKey, string>;
type Flags<T> = Partial<Record<MeasurementKey, T | undefined>>;
const FIELDS: ReadonlyArray<{ key: MeasurementKey; label: string; placeholder: string }> = [
  { key: 'height', label: 'Рост, см', placeholder: 'Например, 172' },
  { key: 'bust', label: 'Обхват груди, см', placeholder: 'Например, 92' },
  { key: 'waist', label: 'Обхват талии, см', placeholder: 'Например, 74' },
  { key: 'hips', label: 'Обхват бедер, см', placeholder: 'Например, 98' },
];
const EMPTY: Fit = { height: '', bust: '', waist: '', hips: '' };

function validate(fit: Fit): Flags<string> {
  const errors: Flags<string> = {};
  for (const { key } of FIELDS) {
    const value = fit[key];
    const [min, max] = MEASUREMENT_BOUNDS[key];
    if (!value) errors[key] = 'Укажите параметр';
    else if (!/^\d+$/.test(value) || Number(value) < min || Number(value) > max)
      errors[key] = 'Проверьте введенное значение';
  }
  return errors;
}

export type SizeAdvisorProps = {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSize: (size: string) => void;
};

/** "Подобрать размер": measurements → recommended size with notes per size. Measurements are not stored. */
export function SizeAdvisor({ product, open, onOpenChange, onSelectSize }: SizeAdvisorProps) {
  const [fit, setFit] = useState<Fit>(EMPTY);
  const [errors, setErrors] = useState<Flags<string>>({});
  const [touched, setTouched] = useState<Flags<boolean>>({});
  const [focus, setFocus] = useState<MeasurementKey | null>(null);
  const [step, setStep] = useState<'input' | 'result'>('input');
  const [advice, setAdvice] = useState<SizeAdvice | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  // Reopening after a recommendation starts at the result (03B openFit).
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open && advice) setStep('result');
  }
  const mutation = useMutation({
    mutationKey: ['size-advice'],
    mutationFn: (input: Record<MeasurementKey, number>) =>
      browserApiRequest('/api/size-advice', SizeAdviceSchema, {
        method: 'POST',
        body: JSON.stringify({ productSlug: product.slug, ...input }),
      }),
    onSuccess: (result) => {
      setAdvice(result);
      setActive(result.recommended);
      setStep('result');
    },
  });
  const filled = FIELDS.every(({ key }) => fit[key].length > 0);
  const loading = mutation.isPending;

  const change = (key: MeasurementKey, value: string) => {
    setFit((current) => ({ ...current, [key]: value.replace(/\D/g, '').slice(0, 3) }));
    setErrors((current) => ({ ...current, [key]: undefined }));
    setTouched((current) => ({ ...current, [key]: true }));
  };

  const blur = (key: MeasurementKey) => {
    setFocus(null);
    if (touched[key] && !fit[key])
      setErrors((current) => ({ ...current, [key]: 'Укажите параметр' }));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!filled || loading) return;
    const found = validate(fit);
    setErrors(found);
    const first = FIELDS.find(({ key }) => found[key]);
    if (first) {
      document.getElementById(`fit-${first.key}`)?.focus();
      return;
    }
    mutation.mutate({
      height: Number(fit.height),
      bust: Number(fit.bust),
      waist: Number(fit.waist),
      hips: Number(fit.hips),
    });
  };

  const close = () => {
    mutation.reset();
    setHelp(false);
    onOpenChange(false);
  };

  // An unavailable size closes the dialog too: the PDP shows its own tip (03B fitConfirm).
  const confirm = () => {
    if (!active) return;
    onSelectSize(active);
    close();
  };

  const onResult = step === 'result' && advice !== null;
  return (
    <Dialog
      theme="editorial"
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title="Подобрать размер"
      width="67.5rem"
      backdrop="strong"
      className={styles.dialog}
    >
      <div className={styles.layout}>
        <div className={styles.photo}>
          <Image
            src="/images/products/top-soft-support.png"
            alt="Модель в спортивном топе и леггинсах J‑VU"
            fill
            sizes="440px"
            className={styles.image}
          />
        </div>
        <div className={styles.body}>
          <div className={styles.top}>
            <p className={styles.eyebrow}>Подобрать размер</p>
            <button
              type="button"
              className={styles.close}
              onClick={close}
              aria-label="Закрыть окно"
            >
              ×
            </button>
          </div>
          <div className={styles.progress}>
            <span className={styles.step}>{onResult ? 'Шаг 2 из 2' : 'Шаг 1 из 2'}</span>
            <ProgressBar
              value={onResult ? 100 : 50}
              label="Прогресс подбора размера"
              track="soft"
            />
          </div>
          {onResult ? (
            <div className={styles.result}>
              <h2 className={styles.title} data-step="result">
                Вам подойдет размер {advice.recommended}
              </h2>
              <p className={styles.caption}>
                Рост {fit.height} см · грудь {fit.bust} см · талия {fit.waist} см · бедра {fit.hips}{' '}
                см · нажмите на размер, чтобы увидеть, как он сядет
              </p>
              <fieldset className={styles.sizes} aria-label="Размеры">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    type="button"
                    className={styles.size}
                    aria-pressed={active === size}
                    data-recommended={size === advice.recommended || undefined}
                    onClick={() => setActive(size)}
                  >
                    <span>{size}</span>
                    <span className={styles.sub}>
                      {size === advice.recommended ? 'рекомендуем' : ''}
                    </span>
                  </button>
                ))}
              </fieldset>
              {active && (
                <div className={styles.explain}>
                  <span className={styles.accent}>Размер {active}</span>
                  <span>{advice.notes[active] ?? ''}</span>
                </div>
              )}
              <div className={styles.actions}>
                <Button size="lg" onClick={confirm}>
                  Выбрать размер {active}
                </Button>
                <Button size="lg" variant="ghost" onClick={() => setStep('input')}>
                  Изменить параметры
                </Button>
              </div>
            </div>
          ) : (
            <form className={styles.form} onSubmit={submit} noValidate>
              <div className={styles.intro}>
                <h2 className={styles.title}>Посадка, которая подходит вам</h2>
                <p className={styles.lead}>
                  Введите 4 измерения в сантиметрах, чтобы получить рекомендацию по размеру
                </p>
                {advice && (
                  <span className={styles.saved}>
                    Ваши параметры сохранены — измените нужные и продолжите
                  </span>
                )}
              </div>
              <div className={styles.fields}>
                {FIELDS.map((field) => {
                  const error = errors[field.key];
                  return (
                    <div key={field.key} className={styles.field}>
                      <label htmlFor={`fit-${field.key}`} className={styles.label}>
                        {field.label}
                      </label>
                      <div
                        className={styles.box}
                        data-filled={fit[field.key] ? true : undefined}
                        data-focus={focus === field.key || undefined}
                        data-invalid={error ? true : undefined}
                      >
                        <input
                          id={`fit-${field.key}`}
                          className={styles.input}
                          type="text"
                          inputMode="numeric"
                          maxLength={3}
                          autoComplete="off"
                          placeholder={field.placeholder}
                          value={fit[field.key]}
                          onChange={(event) => change(field.key, event.target.value)}
                          onFocus={() => setFocus(field.key)}
                          onBlur={() => blur(field.key)}
                          aria-invalid={error ? true : undefined}
                          aria-describedby={`fit-${field.key}-error`}
                        />
                        <span aria-hidden="true" className={styles.unit}>
                          см
                        </span>
                      </div>
                      <span id={`fit-${field.key}-error`} className={styles.error}>
                        {error ?? ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              <button
                type="button"
                className={styles.help}
                onClick={() => setHelp(true)}
                aria-expanded={help}
              >
                <Icon name="tape" size={32} strokeWidth={1.3} />
                <span className={styles['help-text']}>
                  <span className={styles['help-title']}>Как правильно измерить?</span>
                  <span className={styles['help-note']}>
                    Измеряйте сантиметровой лентой без натяжения. Лента должна плотно прилегать, но
                    не сдавливать
                  </span>
                </span>
                <Icon name="chevron-right" size={18} strokeWidth={1.4} />
              </button>
              {mutation.isError && (
                <p className={styles.error} role="alert">
                  {mutation.error?.message}
                </p>
              )}
              <button
                type="submit"
                className={cx(styles.submit)}
                aria-disabled={!filled || undefined}
                aria-busy={loading || undefined}
                data-state={loading ? 'loading' : filled ? 'ready' : 'idle'}
              >
                {loading && <Spinner size={16} />}
                <span>{loading ? 'Подбираем размер…' : 'Продолжить'}</span>
              </button>
              <p className={styles.note}>
                Данные используются только для подбора размера и не сохраняются
              </p>
            </form>
          )}
          {help && (
            <>
              <button
                type="button"
                className={styles.veil}
                onClick={() => setHelp(false)}
                aria-label="Закрыть подсказку об измерениях"
                tabIndex={-1}
              />
              <dialog open className={styles.popup} aria-label="Как правильно измерить">
                <div className={styles.top} data-gap="12">
                  <h3 className={styles['popup-title']}>Как правильно измерить?</h3>
                  <button
                    type="button"
                    className={styles.x}
                    onClick={() => setHelp(false)}
                    aria-label="Закрыть"
                  >
                    ×
                  </button>
                </div>
                <ul className={styles.list}>
                  <li>
                    <b>Рост</b> — измерьте расстояние от макушки до пола без обуви.
                  </li>
                  <li>
                    <b>Обхват груди</b> — проведите ленту по наиболее выступающей части груди.
                  </li>
                  <li>
                    <b>Обхват талии</b> — измерьте самую узкую часть корпуса.
                  </li>
                  <li>
                    <b>Обхват бедер</b> — проведите ленту по наиболее выступающей части бедер.
                  </li>
                </ul>
                <Button
                  variant="ghost"
                  size="sm"
                  className={styles.ok}
                  onClick={() => setHelp(false)}
                >
                  Понятно
                </Button>
              </dialog>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
